import { Component } from '@theme/component';

/**
 * A share button that uses the native OS share sheet when available (mostly mobile),
 * and falls back to a small dropdown with a copy-to-clipboard link on desktop.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} nativeButton - The native share button, hidden unless `navigator.share` is supported.
 * @property {HTMLElement} fallbackWrapper - Wrapper around the fallback trigger + dropdown, hidden when native share is used.
 * @property {HTMLElement} trigger - The fallback dropdown trigger button.
 * @property {HTMLElement} menu - The fallback dropdown panel.
 * @property {HTMLInputElement} input - The read-only input holding the share URL.
 * @property {HTMLElement} copySuccessMessage - The status message revealed after a successful copy.
 *
 * @extends Component<Refs>
 */
class ShareButtonComponent extends Component {
  requiredRefs = ['nativeButton', 'fallbackWrapper', 'trigger', 'menu', 'input', 'copySuccessMessage'];

  connectedCallback() {
    super.connectedCallback();

    if (navigator.share) {
      this.refs.nativeButton.hidden = false;
      this.refs.fallbackWrapper.hidden = true;
    } else {
      document.addEventListener('click', this.#onDocumentClick);
      document.addEventListener('keydown', this.#onDocumentKeydown);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#onDocumentClick);
    document.removeEventListener('keydown', this.#onDocumentKeydown);
  }

  share() {
    const url = this.getAttribute('share-url') || document.location.href;

    navigator.share({ url, title: document.title }).catch(() => {});
  }

  toggleMenu() {
    const isOpen = !this.refs.menu.hidden;

    this.refs.menu.hidden = isOpen;
    this.refs.trigger.setAttribute('aria-expanded', String(!isOpen));
  }

  #closeMenu() {
    this.refs.menu.hidden = true;
    this.refs.trigger.setAttribute('aria-expanded', 'false');
  }

  copyLink() {
    navigator.clipboard.writeText(this.refs.input.value).then(() => {
      this.refs.copySuccessMessage.classList.remove('visually-hidden');
    });
  }

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (!(event.target instanceof Node)) return;
    if (this.refs.menu.hidden) return;
    if (this.contains(event.target)) return;

    this.#closeMenu();
  };

  /** @param {KeyboardEvent} event */
  #onDocumentKeydown = (event) => {
    if (event.key === 'Escape' && !this.refs.menu.hidden) {
      this.#closeMenu();
      this.refs.trigger.focus();
    }
  };
}

if (!customElements.get('share-button-component')) {
  customElements.define('share-button-component', ShareButtonComponent);
}
